import { Component, Input, Output, EventEmitter, forwardRef } from '@angular/core';
import {
  ControlValueAccessor, Validator, AbstractControl, ValidationErrors,
  NG_VALUE_ACCESSOR, NG_VALIDATORS,
} from '@angular/forms';
import {
  MAX_EMAILS, emailParts, isValidEmail, isEmailSeparator, stripEmailSeparators,
} from '../../utils/email.util';

/** Mirrors the separators email.util splits on. */
const SEPARATOR_PRESENT = /[,;]/;

/**
 * Editor for an email field that may hold a primary address and secondary ones.
 *
 * The stored value is a single comma separated string; this component splits it
 * into a row per address, the same shape app-phone-input gives a multi number
 * field, so the two sit alongside each other on a contact form and behave the
 * same way.
 *
 * Commas and semicolons are refused rather than stored: they are what separates
 * one address from the next, so an address containing one would be read back as
 * two. Typed ones are dropped at the keypress; a pasted list is split across
 * rows instead, which is what the user meant by it anyway.
 */
@Component({
  selector: 'app-email-input',
  templateUrl: './email-input.component.html',
  styleUrls: ['./email-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EmailInputComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => EmailInputComponent),
      multi: true,
    },
  ],
})
export class EmailInputComponent implements ControlValueAccessor, Validator {
  /** Addresses the user may add. Existing values above this are still shown. */
  @Input() maxEmails = MAX_EMAILS;
  @Input() placeholder = '';
  /** Names the extra rows for what they are, so the repeater explains itself. */
  @Input() secondaryPlaceholder = 'Additional email';
  @Input() inputId: string = null;
  @Input() readonly = false;
  /** Host forms drive their own error styling; mirror it onto the rows. */
  @Input() invalid = false;
  /**
   * The same, for the primary row alone. The duplicate check only considers
   * the primary address, so painting every row would point the user at
   * addresses that are not what the warning is about.
   */
  @Input() primaryInvalid = false;

  /**
   * Fires after a row has settled, so hosts that react to blur (the contact
   * picker's duplicate check, for one) compare the trimmed value rather than
   * whatever was mid-typing.
   */
  @Output() blurred = new EventEmitter<void>();

  rows: string[] = [''];
  disabled = false;

  /**
   * Hosts pass their form's submitted flag so an address that predates this
   * field surfaces when the form is submitted, rather than only after the user
   * happens to click into it.
   */
  @Input() submitted = false;

  /** Set on blur so the message appears when the user leaves the field. */
  showError = false;

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  /** Hidden once the field is full, so the cap needs no explanation. */
  get canAdd(): boolean {
    return this.editable && this.rows.length < this.maxEmails;
  }

  get editable(): boolean {
    return !this.disabled && !this.readonly;
  }

  get currentlyValid(): boolean {
    return isValidEmail(this.rows.join(', '));
  }

  /** On blur once touched, and on submit for values loaded already bad. */
  get errorVisible(): boolean {
    return this.showError || (this.submitted && !this.currentlyValid);
  }

  /** Whether a given row is painted as invalid. */
  rowInvalid(index: number): boolean {
    return this.invalid || (this.primaryInvalid && index === 0) || this.errorVisible;
  }

  // ControlValueAccessor

  writeValue(value: string) {
    // Show every stored address even if there are more than maxEmails: hiding
    // one would be the silent data loss this component exists to prevent.
    const parts = emailParts(value);
    this.rows = parts.length ? parts : [''];
  }

  registerOnChange(fn: (value: string) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
  }

  // Validator

  validate(control: AbstractControl): ValidationErrors {
    return isValidEmail(control.value) ? null : { email: true };
  }

  registerOnValidatorChange(fn: () => void) {
    this.onValidatorChange = fn;
  }

  private onValidatorChange: () => void = () => {};

  // Template handlers

  /**
   * Swallows a typed comma or semicolon. Stopping it here rather than removing
   * it after the fact means it never appears on screen at all, so there is
   * nothing for the user to wonder about having vanished.
   */
  onRowKeyPress(event: KeyboardEvent) {
    // Enter, Tab and the like arrive as multi-character key names and must not
    // be swallowed along with the separators.
    if (event.key && event.key.length === 1 && isEmailSeparator(event.key)) {
      event.preventDefault();
    }
  }

  /**
   * A pasted "a@b.com, c@d.com" is split across rows here rather than on blur:
   * the separator cannot be typed, so its only source is a paste, and leaving
   * it sitting in the box until blur would show the user a value the field has
   * just told them it will not take.
   */
  onRowInput(index: number, event: Event) {
    const input = event.target as HTMLInputElement;

    if (SEPARATOR_PRESENT.test(input.value)) {
      const parts = emailParts(input.value);

      this.rows.splice.apply(this.rows, [index, 1].concat(
        (parts.length ? parts : ['']) as any[]
      ));
    } else {
      this.rows[index] = input.value;
    }

    // Clear a previous complaint as soon as they start correcting it.
    this.showError = false;
    this.emit();
  }

  /** Settles the row on the way out. */
  onRowBlur(index: number) {
    // Autofill and drag-and-drop reach the value without a keypress, so the
    // separators are stripped once more here rather than trusted to have been
    // caught on the way in.
    this.rows[index] = stripEmailSeparators(this.rows[index] || '').trim();

    // Only judge once the user has left the field, so a half typed address is
    // never flagged mid-keystroke.
    this.showError = !this.currentlyValid;

    this.onTouched();
    this.emit();
    this.blurred.emit();
  }

  /** Empties the row without removing it, so the field stays there to retype into. */
  clearRow(index: number) {
    this.rows[index] = '';
    this.showError = false;
    this.emit();
  }

  addRow() {
    this.rows.push('');
  }

  removeRow(index: number) {
    this.rows.splice(index, 1);
    if (!this.rows.length) {
      this.rows = [''];
    }
    this.showError = !this.currentlyValid;
    this.emit();
  }

  trackByIndex(index: number): number {
    return index;
  }

  private emit() {
    const filled = this.rows
      .map(row => (row || '').trim())
      .filter(row => row !== '');

    this.onChange(filled.join(', '));
    this.onValidatorChange();
  }
}
